/* eslint-disable require-atomic-updates */
import {Nilable, isThemeEditor} from 'utilities/common';

import {UnableToResolveVariantError} from './errors';
import {ProductDetails, ProductOption, ProductVariant} from './types';

export const mockProductDetailsForThemeEditor = {
  variants: [
    {
      id: 1,
      available: true,
    },
  ],
} as ProductDetails;

const inFlightRequests: {[url: string]: Promise<ProductDetails> | null} = {};
const cachedProductDetails: {[url: string]: ProductDetails | null} = {};

export function getProductPath() {
  const collectionsRegex = /\/collections\/.*\/products/;
  return window.location.pathname
    .replace(collectionsRegex, '/products')
    .replace(/\/+$/, '');
}

export async function fetchProductDetails() {
  const productUrl = `${getProductPath()}.js`;

  if (cachedProductDetails[productUrl]) {
    return cachedProductDetails[productUrl];
  }

  if (isThemeEditor()) {
    return mockProductDetailsForThemeEditor;
  }

  if (!inFlightRequests[productUrl]) {
    inFlightRequests[productUrl] = fetch(productUrl).then((response) =>
      response.ok ? response.json() : null,
    );
  }

  const result = await inFlightRequests[productUrl];

  cachedProductDetails[productUrl] = result || productFromShopifyAnalytics();

  return cachedProductDetails[productUrl];
}

export function getAddToCartForm() {
  const forms = [
    ...document.querySelectorAll<HTMLFormElement>(
      'form[action*="/cart/add"]:not([id*=installment]):not([id*=grid-product-form]):not([id*=list-product-form]):not([id*=recently]):not([id*=swatch-form]):not([id*=list-result]):not([id*=sticky]):not([id*=product_form_mini-cart])',
    ),
  ];

  // form must be visible and must contain a button with specific text that is visible
  const applicableForm = forms.find(
    (form) => Boolean(form.offsetParent) && getAddToCartButton(form) !== null,
  );

  return applicableForm ?? null;

  function getAddToCartButton(addToCartForm: HTMLElement): HTMLElement | null {
    const formButtons = addToCartForm.querySelectorAll(
      'a, button, div, input[type="button"], input[type="submit"], span',
    ) as NodeListOf<HTMLInputElement>;
    for (const button of formButtons) {
      const buttonText = button.value || button.textContent;
      if (
        (isAddToCartString(buttonText) || isOutOfStockString(buttonText)) &&
        isDisplayed(button)
      ) {
        return button;
      }
    }
    return null;

    function isAddToCartString(str: string | null): boolean {
      if (!str) return false;
      const addToCartStrings = [
        'ADD THIS ITEM',
        'ADD TO BAG',
        'ADD TO BASKET',
        'ADD TO CART',
        'BUY NOW',
        'BUY ONCE',
        'CHECKOUT',
        'GET IN MY BAG',
        'ONE TIME PURCHASE',
        'ONE-TIME PURCHASE',
        'SINGLE PURCHASE',
        'TRY NOW',
      ];
      return addToCartStrings.some((addToCartString) =>
        str.toUpperCase().includes(addToCartString),
      );
    }

    function isOutOfStockString(str: string | null): boolean {
      if (!str) return false;
      const outOfStockStrings = ['OUT OF STOCK', 'SOLD OUT'];
      return outOfStockStrings.some((outOfStockString) =>
        str.toUpperCase().includes(outOfStockString),
      );
    }
  }
}

export function isDisplayed(
  element: HTMLElement,
  allowOverflow = true,
): boolean {
  const styles = getComputedStyle(element);

  if (!allowOverflow && styles.overflow === 'hidden') {
    return false;
  }

  return (
    styles.display !== 'none' &&
    styles.visibility !== 'hidden' &&
    styles.height !== '0px' &&
    styles.width !== '0px'
  );
}

export function resolveVariant(productDetails: ProductDetails | null) {
  if (!productDetails) {
    return null;
  }

  if (productDetails.variants.length === 1) {
    return productDetails.variants[0];
  }

  const addToCartForm = getAddToCartForm();

  const formData = getFormData(addToCartForm);

  // todo: rename functions
  const fn1 = variantMatchesToUrl;
  const fn2 = (variant: ProductVariant) =>
    matchesVariantOption(
      variant,
      extractVariantOptionsFromForm(formData, productDetails),
    );
  const fn3 = (variant: ProductVariant) =>
    matchesVariantOption(
      variant,
      extractVariantOptionsFromHtmlDataIndexAttribute(),
    );
  const fn4 = (variant: ProductVariant) =>
    matchesVariantOption(
      variant,
      extractVariantOptionsFromHtmlNameAttribute(addToCartForm),
    );
  const fn5 = (variant: ProductVariant) => {
    return variant.id === extractVariantIdFromFormElements(addToCartForm);
  };
  const fn6 = (variant: ProductVariant) => {
    return variant.id === extractVariantIdFromShopifyAnalytics();
  };
  const fn7 = variantIsAvailable;

  const variantMatchers: ((variant: ProductVariant) => boolean)[] = [
    fn1,
    fn2,
    fn3,
    fn4,
    fn5,
    fn6,
    fn7,
  ];

  let foundVariant: Nilable<ProductVariant> = null;
  for (const matcher of variantMatchers) {
    foundVariant = productDetails.variants.find(matcher);
    if (foundVariant) {
      break;
    }
  }

  if (!foundVariant) {
    throw new UnableToResolveVariantError();
  }

  return foundVariant;

  /**
   *
   * @param variant
   * @param optionData
   */
  function matchesVariantOption(
    variant: ProductVariant,
    optionData?: VariantOptionData,
  ): boolean {
    if (!optionData) {
      return false;
    }

    return (
      variant.option1 === (optionData.option1 || null) &&
      variant.option2 === (optionData.option2 || null) &&
      variant.option3 === (optionData.option3 || null)
    );
  }
}

export function resolveSellingPlanId() {
  const addToCartForm = getAddToCartForm();

  const f1 = () =>
    addToCartForm && extractSellingPlanFromHtmlNameAttribute(addToCartForm);
  const f2 = extractSellingPlanFromURL;

  const strategies = [f1, f2];

  for (const strategy of strategies) {
    const sellingPlan = strategy();
    if (sellingPlan) {
      return sellingPlan;
    }
  }

  return null;
}

type SellingPlanElement = HTMLInputElement | HTMLSelectElement;

interface ProductFormElement extends HTMLCollection {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  selling_plan?: SellingPlanElement;
  id: HTMLInputElement;
}

export function extractSellingPlanFromHtmlNameAttribute(form: HTMLFormElement) {
  if (!form) return null;
  const sellingPlanElement = (form.elements as ProductFormElement).selling_plan;
  if (!sellingPlanElement) return null;
  const {value} = sellingPlanElement;
  return value ? parseInt(value, 10) : null;
}

function extractSellingPlanFromURL() {
  const sellingPlanURLParam = new URLSearchParams(window.location.search).get(
    'selling_plan',
  );
  return sellingPlanURLParam ? parseInt(sellingPlanURLParam, 10) : null;
}

interface VariantOptionData {
  option1: string | null;
  option2: string | null;
  option3: string | null;
}

export function extractVariantOptionsFromForm(
  formData: FormData | null,
  productDetails: ProductDetails,
) {
  if (!formData || !productDetails.options) {
    return undefined;
  }

  const optionData: VariantOptionData = {
    option1: optionValue(formData, productDetails.options[0]),
    option2: optionValue(formData, productDetails.options[1]),
    option3: optionValue(formData, productDetails.options[2]),
  };

  return optionData;

  function optionValue(formData: FormData, option: ProductOption | null) {
    if (!option) {
      return null;
    }

    if (option.values.length === 1) {
      return option.values[0];
    }

    let index = option.position;
    if (formData?.has('option-0')) {
      index = option.position - 1;
    }
    return (
      (formData?.get(option.name) as string) ||
      (formData?.get(`option-${index}`) as string) ||
      (formData?.get(`options[${option.name}]`) as string)
    );
  }
}

function extractVariantIdFromFormElements(form: HTMLFormElement | null) {
  if (!form) {
    return undefined;
  }

  const elements = form.elements as ProductFormElement;

  if (elements && elements.id && elements.id.value) {
    return Number(elements?.id?.value);
  } else {
    return null;
  }
}

function extractVariantIdFromShopifyAnalytics() {
  const variantId = window?.ShopifyAnalytics?.meta?.selectedVariantId;
  return variantId ? Number(variantId) : null;
}

interface DataHTMLElement extends HTMLElement {
  checked?: boolean;
  dataset: {
    index: string;
  };
  value: string | number | null;
}

function extractVariantOptionsFromHtmlDataIndexAttribute() {
  const dataElements = Array.from(
    document.querySelectorAll('[data-index]'),
  ) as DataHTMLElement[];

  return dataElements.reduce(
    (optionData, element) => {
      if (element.nodeName !== 'INPUT' || element.checked) {
        optionData[element.dataset.index] = element.value;
      }
      return optionData;
    },
    {
      option1: null,
      option2: null,
      option3: null,
    } as any,
  ) as VariantOptionData;
}

function extractVariantOptionsFromHtmlNameAttribute(
  addToCartForm: HTMLElement | null,
) {
  if (!addToCartForm) {
    return undefined;
  }

  const optionElements = Array.from(
    addToCartForm.querySelectorAll(
      'input[name^="option"][checked], select[name^="option"]',
    ),
  ) as (HTMLSelectElement | HTMLInputElement)[];

  const sortedOptionElements = optionElements.sort((option1, option2) =>
    option1.name.localeCompare(option2.name),
  );
  const variantOptionData = sortedOptionElements.reduce(
    (optionData, optionElement, index) => {
      optionData[`option${index + 1}`] = optionElement.value;
      return optionData;
    },

    {} as any,
  );

  return variantOptionData as VariantOptionData;
}

function variantMatchesToUrl(variant: ProductVariant) {
  const variantId = parseInt(
    new URLSearchParams(window.location.search).get('variant') ?? '',
    10,
  );

  return variant.id === variantId;
}

function variantIsAvailable(variant: ProductVariant) {
  return variant.available;
}

function getFormData(form: HTMLElement | null) {
  if (form && form.tagName === 'FORM') {
    return new FormData(form as HTMLFormElement);
  }
  return null;
}

export function productFromShopifyAnalytics() {
  return window?.ShopifyAnalytics?.meta?.product || null;
}

export function getFromBoomr(key: keyof typeof window.BOOMR) {
  return window?.BOOMR?.[key] ?? null;
}
